import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../network/networkConnection';
import { NetworkConfiguration } from '../../network/networkConfriguation';

// Initial state
const initialState = {
  addresses: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  defaultShippingAddress: null,
  defaultBillingAddress: null,
};

// Async thunks for API calls
export const fetchAddresses = createAsyncThunk('address/fetchAddresses', async (_, { rejectWithValue }) => {
  try {
    console.log('Fetching addresses from:', process.env.REACT_APP_APIUrl + NetworkConfiguration.GET_ADDRESSES);
    const response = await callAPI(process.env.REACT_APP_APIUrl + NetworkConfiguration.GET_ADDRESSES, 'GET', { 'Content-Type': 'application/json' });
    console.log('Fetch addresses API response:', response);
    if (response.status?.code === 200) {
      console.log('Addresses data:', response.data);
      return response.data;
    }
    console.error('API returned non-200 status:', response.status);
    return rejectWithValue(response.error?.message || 'Failed to fetch addresses');
  } catch (error) {
    console.error('Error in fetchAddresses:', error);
    return rejectWithValue(error.message || 'Failed to fetch addresses');
  }
});

export const addAddress = createAsyncThunk('address/addAddress', async (addressData, { rejectWithValue }) => {
  try {
    const response = await callAPI(process.env.REACT_APP_APIUrl + NetworkConfiguration.ADD_ADDRESS, 'POST', { 'Content-Type': 'application/json' }, JSON.stringify(addressData));
    if (response.status?.code === 200) {
      return response.data;
    }
    return rejectWithValue(response.error?.message || 'Failed to add address');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to add address');
  }
});

export const updateAddress = createAsyncThunk('address/updateAddress', async ({ addressId, addressData }, { rejectWithValue }) => {
  try {
    const response = await callAPI(
      process.env.REACT_APP_APIUrl + `${NetworkConfiguration.UPDATE_ADDRESS}/${addressId}`,
      'PUT',
      { 'Content-Type': 'application/json' },
      JSON.stringify(addressData)
    );
    if (response.status?.code === 200) {
      return response.data;
    }
    return rejectWithValue(response.error?.message || 'Failed to update address');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to update address');
  }
});

export const deleteAddress = createAsyncThunk('address/deleteAddress', async (addressId, { rejectWithValue }) => {
  try {
    const response = await callAPI(process.env.REACT_APP_APIUrl + `${NetworkConfiguration.DELETE_ADDRESS}/${addressId}`, 'DELETE', { 'Content-Type': 'application/json' });
    if (response.status?.code === 200) {
      return addressId;
    }
    return rejectWithValue(response.error?.message || 'Failed to delete address');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to delete address');
  }
});

export const setDefaultAddress = createAsyncThunk('address/setDefaultAddress', async ({ addressId, type }, { rejectWithValue }) => {
  try {
    const response = await callAPI(
      process.env.REACT_APP_APIUrl + `${NetworkConfiguration.SET_DEFAULT_ADDRESS}/${addressId}/set-default`,
      'PUT',
      { 'Content-Type': 'application/json' },
      JSON.stringify({ type })
    );
    if (response.status?.code === 200) {
      return response.data;
    }
    return rejectWithValue(response.error?.message || 'Failed to set default address');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to set default address');
  }
});

// Address slice
const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    clearAddresses: state => {
      state.addresses = [];
      state.defaultShippingAddress = null;
      state.defaultBillingAddress = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch addresses
      .addCase(fetchAddresses.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.addresses = action.payload;
        state.defaultShippingAddress = action.payload.find(addr => addr.isDefaultShipping) || null;
        state.defaultBillingAddress = action.payload.find(addr => addr.isDefaultBilling) || null;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Add address
      .addCase(addAddress.pending, state => {
        state.status = 'loading';
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const newAddress = action.payload;
        state.addresses.push(newAddress);

        // Update defaults if the new address is default
        // Also remove default flag from other addresses
        if (newAddress.isDefaultShipping) {
          // Remove isDefaultShipping from all other addresses
          state.addresses.forEach(addr => {
            if (addr._id !== newAddress._id && addr.isDefaultShipping) {
              addr.isDefaultShipping = false;
            }
          });
          state.defaultShippingAddress = newAddress;
        }
        if (newAddress.isDefaultBilling) {
          // Remove isDefaultBilling from all other addresses
          state.addresses.forEach(addr => {
            if (addr._id !== newAddress._id && addr.isDefaultBilling) {
              addr.isDefaultBilling = false;
            }
          });
          state.defaultBillingAddress = newAddress;
        }
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Update address
      .addCase(updateAddress.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updatedAddress = action.payload;
        const index = state.addresses.findIndex(addr => addr._id === updatedAddress._id);

        if (index !== -1) {
          state.addresses[index] = updatedAddress;
        }

        // Update defaults if the updated address is default
        // Also remove default flag from other addresses
        if (updatedAddress.isDefaultShipping) {
          // Remove isDefaultShipping from all other addresses
          state.addresses.forEach((addr, i) => {
            if (i !== index && addr.isDefaultShipping) {
              addr.isDefaultShipping = false;
            }
          });
          state.defaultShippingAddress = updatedAddress;
        }
        if (updatedAddress.isDefaultBilling) {
          // Remove isDefaultBilling from all other addresses
          state.addresses.forEach((addr, i) => {
            if (i !== index && addr.isDefaultBilling) {
              addr.isDefaultBilling = false;
            }
          });
          state.defaultBillingAddress = updatedAddress;
        }
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Delete address
      .addCase(deleteAddress.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.addresses = state.addresses.filter(addr => addr._id !== action.payload);

        // Update defaults if the deleted address was default
        if (state.defaultShippingAddress?._id === action.payload) {
          state.defaultShippingAddress = state.addresses.find(addr => addr.isDefaultShipping) || null;
        }
        if (state.defaultBillingAddress?._id === action.payload) {
          state.defaultBillingAddress = state.addresses.find(addr => addr.isDefaultBilling) || null;
        }
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Set default address
      .addCase(setDefaultAddress.pending, state => {
        state.status = 'loading';
      })
      .addCase(setDefaultAddress.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updatedAddress = action.payload;
        const index = state.addresses.findIndex(addr => addr._id === updatedAddress._id);

        if (index !== -1) {
          // Update the address in the array
          state.addresses[index] = updatedAddress;

          // Update defaults
          if (updatedAddress.isDefaultShipping) {
            // Remove default from other addresses
            state.addresses.forEach((addr, i) => {
              if (i !== index) {
                addr.isDefaultShipping = false;
              }
            });
            state.defaultShippingAddress = updatedAddress;
          }
          if (updatedAddress.isDefaultBilling) {
            // Remove default from other addresses
            state.addresses.forEach((addr, i) => {
              if (i !== index) {
                addr.isDefaultBilling = false;
              }
            });
            state.defaultBillingAddress = updatedAddress;
          }
        }
      })
      .addCase(setDefaultAddress.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

// Selectors
export const selectAddresses = state => state.address.addresses;
export const selectAddressStatus = state => state.address.status;
export const selectAddressError = state => state.address.error;
export const selectDefaultShippingAddress = state => state.address.defaultShippingAddress;
export const selectDefaultBillingAddress = state => state.address.defaultBillingAddress;

// Actions
export const { clearAddresses } = addressSlice.actions;

export default addressSlice.reducer;
